"use client";

import React, { useEffect, useRef, useState } from "react";

import { BREAKPOINTS, queryUp } from "@/lib/breakpoints";

export type ConsentSettings = {
  necessary: boolean;
  personalization: boolean;
  marketing: boolean;
  statistics: boolean;
};

export type CookieConsentTexts = {
  title: string;
  description: string;
  privacyLinkText: string;
  cookiePolicyLinkText: string;
  imprintLinkText?: string;
  categories: {
    necessary: { name: string; description: string };
    personalization: { name: string; description: string };
    marketing: { name: string; description: string };
    statistics: { name: string; description: string };
  };
  buttons: {
    acceptAll: string;
    acceptNecessary: string;
    saveSelection: string;
    managePreferences: string;
    collapse: string;
  };
};

export type CookieConsentProps = {
  cookieName?: string;
  expiryDays?: number;
  privacyUrl: string;
  cookiePolicyUrl: string;
  imprintUrl?: string;
  texts: CookieConsentTexts;
  onConsentChange?: (consent: ConsentSettings) => void;
  className?: string;
  /** Whether to inject a default minimal stylesheet if you do not provide your own */
  injectDefaultStyles?: boolean;
};

const DEFAULT_STYLES = `
.cookie-consent-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 9998;
}
.cookie-consent-panel {
  /* Floating bottom-center notice card. First layer is NON-BLOCKING: there is
     no full-page backdrop, so the page stays fully clickable underneath and the
     visitor never hits a dead click. The backdrop only appears when the
     preferences layer is expanded (a true second-layer modal). */
  position: fixed;
  left: 50%;
  bottom: 1.25rem;
  transform: translateX(-50%);
  width: min(42rem, calc(100vw - 1.5rem));
  max-height: calc(100vh - 2.5rem);
  overflow-y: auto;
  z-index: 9999;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-top: 3px solid #0AB3BF; /* brand accent — unmistakably an interactive notice */
  border-radius: 0.75rem;
  box-shadow: 0 24px 60px -12px rgba(0,0,0,0.35);
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}
/* Dark mode: the site uses class-based dark mode (html.dark). Match the
   slate-950 / slate-900 palette used by the rest of the public layout. */
html.dark .cookie-consent-panel {
  background: #0f172a;
  border-color: #1e293b;
  border-top-color: #0AB3BF;
  box-shadow: 0 24px 60px -12px rgba(0,0,0,0.7);
}
.cookie-consent-inner {
  padding: 1.25rem 1.5rem 1.5rem;
}
.cookie-consent-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}
.cookie-consent-title {
  margin: 0 0 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
}
html.dark .cookie-consent-title {
  color: #f8fafc;
}
.cookie-consent-description {
  margin: 0;
  font-size: 0.875rem;
  color: #4b5563;
  line-height: 1.5;
}
html.dark .cookie-consent-description {
  color: #cbd5e1;
}
.cookie-consent-description a {
  color: #2563eb;
  text-decoration: underline;
}
html.dark .cookie-consent-description a {
  color: #60a5fa;
}
.cookie-consent-categories {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
}
@media ${queryUp(BREAKPOINTS.md)} {
  .cookie-consent-categories {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
.cookie-consent-category {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
}
html.dark .cookie-consent-category {
  border-color: #334155;
}
.cookie-consent-category input[type="checkbox"] {
  margin-top: 0.25rem;
}
.cookie-consent-category-name {
  font-weight: 600;
  font-size: 0.875rem;
}
html.dark .cookie-consent-category-name {
  color: #f1f5f9;
}
.cookie-consent-category-desc {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.125rem;
}
html.dark .cookie-consent-category-desc {
  color: #94a3b8;
}
.cookie-consent-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 1rem;
}
.cookie-consent-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.cookie-consent-btn {
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
}
.cookie-consent-btn-primary {
  background: #0AB3BF;
  color: #fff;
}
html.dark .cookie-consent-btn-primary {
  background: #0AB3BF;
  color: #fff;
}
.cookie-consent-btn-secondary {
  background: #fff;
  color: #111827;
  border-color: #d1d5db;
}
html.dark .cookie-consent-btn-secondary {
  background: #1e293b;
  color: #e2e8f0;
  border-color: #475569;
}
.cookie-consent-link {
  font-size: 0.875rem;
  color: #4b5563;
  text-decoration: underline;
}
html.dark .cookie-consent-link {
  color: #94a3b8;
}
/* Attention nudge: a brief teal pulse when the visitor FIRST clicks anywhere else
   while the (non-blocking) consent notice is still open — so they notice the
   choice is waiting. translateX(-50%) is preserved because the panel is
   center-anchored; after the animation the static box-shadow rule resumes. */
@keyframes cookie-consent-nudge {
  0%   { transform: translateX(-50%) scale(1);     box-shadow: 0 0 0 0 rgba(10,179,191,0), 0 24px 60px -12px rgba(0,0,0,0.35); }
  30%  { transform: translateX(-50%) scale(1.015); box-shadow: 0 0 0 7px rgba(10,179,191,0.30), 0 24px 60px -12px rgba(0,0,0,0.40); }
  60%  { transform: translateX(-50%) scale(1.01);  box-shadow: 0 0 0 3px rgba(10,179,191,0.38), 0 24px 60px -12px rgba(0,0,0,0.40); }
  100% { transform: translateX(-50%) scale(1);     box-shadow: 0 0 0 0 rgba(10,179,191,0), 0 24px 60px -12px rgba(0,0,0,0.35); }
}
.cookie-consent-nudge {
  animation: cookie-consent-nudge 0.9s ease;
}
`;

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

function setCookie(name: string, value: string, days: number) {
  if (typeof document === "undefined") return;
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = "; expires=" + date.toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}${expires}; path=/; SameSite=Lax`;
}

export function useCookieConsent(
  cookieName = "cookie_consent",
  expiryDays = 365
): {
  consent: ConsentSettings | null;
  saveConsent: (next: ConsentSettings) => void;
} {
  const [consent, setConsent] = useState<ConsentSettings | null>(null);

  useEffect(() => {
    const existing = getCookie(cookieName);
    if (existing) {
      try {
        const parsed = JSON.parse(decodeURIComponent(existing)) as ConsentSettings;
        setConsent({ ...parsed, necessary: true });
      } catch {
        setConsent(null);
      }
    }
  }, [cookieName]);

  const saveConsent = (next: ConsentSettings) => {
    const withNecessary = { ...next, necessary: true };
    setCookie(cookieName, JSON.stringify(withNecessary), expiryDays);
    setConsent(withNecessary);
  };

  return { consent, saveConsent };
}

export default function CookieConsent({
  cookieName = "cookie_consent",
  expiryDays = 365,
  privacyUrl,
  cookiePolicyUrl,
  imprintUrl,
  texts,
  onConsentChange,
  className = "",
  injectDefaultStyles = true,
}: CookieConsentProps) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [nudged, setNudged] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [settings, setSettings] = useState<ConsentSettings>({
    necessary: true,
    personalization: false,
    marketing: false,
    statistics: false,
  });

  useEffect(() => {
    if (typeof document === "undefined") return;
    const existing = getCookie(cookieName);
    if (!existing) {
      setOpen(true);
    } else {
      try {
        const parsed = JSON.parse(decodeURIComponent(existing)) as ConsentSettings;
        setSettings({ ...parsed, necessary: true });
      } catch {
        setOpen(true);
      }
    }
  }, [cookieName]);

  useEffect(() => {
    if (injectDefaultStyles && typeof document !== "undefined") {
      const id = "cookie-consent-default-styles";
      if (!document.getElementById(id)) {
        const style = document.createElement("style");
        style.id = id;
        style.textContent = DEFAULT_STYLES;
        document.head.appendChild(style);
      }
    }
  }, [injectDefaultStyles]);

  // One-time attention nudge: while the first-layer notice is open and consent is
  // still pending, the visitor's FIRST click anywhere else on the page briefly
  // highlights the notice. The click is NOT swallowed — it still works normally,
  // so there is no dead click; the nudge just draws the eye to the waiting choice.
  useEffect(() => {
    if (!open || nudged) return;
    const onPointerDown = (e: PointerEvent) => {
      if (panelRef.current?.contains(e.target as Node)) return; // already on the notice
      setNudged(true);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open, nudged]);

  const save = (next: ConsentSettings) => {
    const withNecessary = { ...next, necessary: true };
    setCookie(cookieName, JSON.stringify(withNecessary), expiryDays);
    setSettings(withNecessary);
    setOpen(false);
    onConsentChange?.(withNecessary);
  };

  const acceptAll = () =>
    save({
      necessary: true,
      personalization: true,
      marketing: true,
      statistics: true,
    });

  const acceptNecessaryOnly = () =>
    save({
      necessary: true,
      personalization: false,
      marketing: false,
      statistics: false,
    });

  const saveSelection = () => save(settings);

  const descriptionHtml = texts.description
    .replace(
      "{privacyLink}",
      `<a href="${privacyUrl}">${texts.privacyLinkText}</a>`
    )
    .replace(
      "{cookiePolicyLink}",
      `<a href="${cookiePolicyUrl}">${texts.cookiePolicyLinkText}</a>`
    );

  if (!open) return null;

  return (
    <>
      {expanded && (
        <div className="cookie-consent-backdrop" onClick={() => setExpanded(false)} />
      )}
      <div
        ref={panelRef}
        className={`cookie-consent-panel ${className}${nudged ? " cookie-consent-nudge" : ""}`}
        role="dialog"
        aria-modal={expanded ? "true" : "false"}
        aria-label={texts.title}
      >
        <div className="cookie-consent-inner">
          <div className="cookie-consent-header">
            <div>
              <h3 className="cookie-consent-title">{texts.title}</h3>
              <p
                className="cookie-consent-description"
                dangerouslySetInnerHTML={{ __html: descriptionHtml }}
              />
            </div>
          </div>

          {expanded && (
            <div className="cookie-consent-categories">
              <label className="cookie-consent-category">
                <input type="checkbox" checked disabled />
                <div>
                  <div className="cookie-consent-category-name">{texts.categories.necessary.name}</div>
                  <div className="cookie-consent-category-desc">{texts.categories.necessary.description}</div>
                </div>
              </label>

              <label className="cookie-consent-category">
                <input
                  type="checkbox"
                  checked={settings.personalization}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, personalization: e.target.checked }))
                  }
                />
                <div>
                  <div className="cookie-consent-category-name">{texts.categories.personalization.name}</div>
                  <div className="cookie-consent-category-desc">{texts.categories.personalization.description}</div>
                </div>
              </label>

              <label className="cookie-consent-category">
                <input
                  type="checkbox"
                  checked={settings.marketing}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, marketing: e.target.checked }))
                  }
                />
                <div>
                  <div className="cookie-consent-category-name">{texts.categories.marketing.name}</div>
                  <div className="cookie-consent-category-desc">{texts.categories.marketing.description}</div>
                </div>
              </label>

              <label className="cookie-consent-category">
                <input
                  type="checkbox"
                  checked={settings.statistics}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, statistics: e.target.checked }))
                  }
                />
                <div>
                  <div className="cookie-consent-category-name">{texts.categories.statistics.name}</div>
                  <div className="cookie-consent-category-desc">{texts.categories.statistics.description}</div>
                </div>
              </label>
            </div>
          )}

          <div className="cookie-consent-actions">
            <div>
              {imprintUrl && (
                <a href={imprintUrl} className="cookie-consent-link">
                  {texts.imprintLinkText}
                </a>
              )}
            </div>
            <div className="cookie-consent-buttons">
              <button className="cookie-consent-btn cookie-consent-btn-secondary" onClick={acceptNecessaryOnly}>
                {texts.buttons.acceptNecessary}
              </button>
              {expanded ? (
                <button className="cookie-consent-btn cookie-consent-btn-secondary" onClick={saveSelection}>
                  {texts.buttons.saveSelection}
                </button>
              ) : (
                <button className="cookie-consent-btn cookie-consent-btn-secondary" onClick={() => setExpanded(true)}>
                  {texts.buttons.managePreferences}
                </button>
              )}
              <button className="cookie-consent-btn cookie-consent-btn-primary" onClick={acceptAll}>
                {texts.buttons.acceptAll}
              </button>
              {expanded && (
                <button className="cookie-consent-btn cookie-consent-btn-secondary" onClick={() => setExpanded(false)}>
                  {texts.buttons.collapse}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
